import {styled, clsx} from 'external/material'
import {React, useMemo, Link} from 'external/react'
import {formatDate} from 'external/date-fns'
import { pdfPreviewLink, viewerGuard} from 'helpers/url'
import IconLink from './IconLink'
import GroupLabel from './GroupLabel'
import CountLabel from './CountLabel'

const StyledCourse = styled('div')(courseStyles)

const Course = props => {
  const {course, courseName, unitCount, lessonCount, groups, context} = props
  
  const activeGroups = useMemo(() => {
    return groups.filter(({subscription}) => 
      !subscription || 
      subscription.subscriptionMode === undefined || 
      subscription.subscriptionStatus === 'active'
    )
  }, [groups])
  
  const expiredGroups = useMemo(() => {
    return groups.filter(({subscription}) => 
      subscription && subscription.subscriptionStatus === 'expired'
    )
  }, [groups])
  
  const groupLabels = useMemo(() => activeGroups.map((group, index, groups) => (
    <GroupLabel key={index} groups={groups} index={index} {...group}/>
  )), [activeGroups])
  
  const expiredGroupLabels = useMemo(() => expiredGroups.map((group, index, groups) => (
    <GroupLabel key={`expired-${index}`} groups={groups} index={index} {...group}/>
  )), [expiredGroups])
  
  const disabled = useMemo(() => activeGroups.length == 0, [activeGroups])
  
  // Find the most recent expired subscription date
  const lastActiveSubscriptionDate = useMemo(() => {
    if (activeGroups.length > 0) return null;
    
    const expiredSubscriptions = groups
      .filter(({subscription}) => 
        subscription && 
        subscription.subscriptionStatus === 'expired' && 
        subscription.subscriptionEnds
      )
      .map(({subscription}) => subscription.subscriptionEnds)
      .sort((a, b) => new Date(b) - new Date(a)); // Sort descending to get most recent first
    
    return expiredSubscriptions.length > 0 ? expiredSubscriptions[0] : null;
  }, [groups, activeGroups]);
  
  const {languageId, link, translate} = context

const overviewLabel = /^C8/.test(course) ? 'dgenius_overview_label' : 'overview_label'
  
  return (
    <StyledCourse key={course} className={clsx('course', {disabled})}>
      <Link className="course-name" to={link({course})}>
        <span className="color-band" style={{backgroundColor: props.subjectColor}}/>
        <span className="course-label">
          {courseName}
        </span>
      </Link>
      <div className="counts-wrapper">
        <div className="course-counts">
          <CountLabel 
            className="unit-count" 
            count={unitCount} 
            label={`unit${unitCount > 1 ? 's' : ''}_label`} 
          />
          <div className="separator">&nbsp;</div>
          <CountLabel 
            className="lesson-count" 
            count={lessonCount} 
            label={`lesson${lessonCount > 1 ? 's' : ''}_label`} 
          />
        </div>
      </div>
      <div className="groups-wrapper">
        <div className="course-groups">
          {activeGroups.length > 0 && expiredGroups.length == 0 ? (
            // Only active groups
            <>
              <div className="groups-label">
                {translate(`group${groupLabels.length > 1 ? 's' : ''}_label`)}:
              </div>
              <div className="group-names">
                {groupLabels}
              </div>
            </>
          ) : activeGroups.length > 0 && expiredGroups.length > 0 ? (
            // Both active and expired groups
            <>
              <div className="groups-label">
                {translate(`group${groupLabels.length > 1 ? 's' : ''}_label`)}:
              </div>
              <div className="group-names">
                {groupLabels}
              </div>
              <div className="expired-groups-label">
                {translate(`expired-group${expiredGroupLabels.length > 1 ? 's' : ''}_label`)}:
              </div>
              <div className="expired-group-names">
                {expiredGroupLabels}
              </div>
            </>
          ) : (
            // Only expired groups or no groups
            <div className="last-subscription-info">
              <div className="last-subscription-label">
                {translate('last-active-subscription')}:
              </div>
              <div className="last-subscription-date">
                {lastActiveSubscriptionDate ? 
                  formatDate(lastActiveSubscriptionDate, 'MMM d, yyyy', languageId) : 
                  translate('no-subscription-found')
                }
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="spacer">&nbsp;</div>
      <div className="links-wrapper">
        <div className="course-links">
          <div className={`overview-link ${props.hasOverview ? 'active' : 'inactive'}`}>
            <IconLink
              {...viewerGuard({href: pdfPreviewLink('download', 'overview', languageId, course), target: "_blank"})}
              icon='overview'
              label={overviewLabel}
            />
          </div>
          <div className={clsx('separator', {inactive: !props.hasGuide})}>&nbsp;</div>
          <div className={`guide-link ${props.hasGuide ? 'active' : 'inactive'}`}>
            <IconLink
              {...viewerGuard({href: pdfPreviewLink('download', 'guide', languageId, course), target: "_blank"})}
              icon='guide'
              label='guide_label'
            />
          </div>
        </div>
      </div>
    </StyledCourse>
  )
}

function courseStyles() {
  return {
    "&.disabled": {
      opacity: 0.8,
      pointerEvents: 'none',
      filter: 'grayscale(0.3)',
      
      '& .course-name': {
        cursor: 'default'
      },
      
      '& .course-links': {
        '& .overview-link, & .guide-link': {
          opacity: 0.3
        }
      },
      
      '& .expired-groups-label, & .expired-group-names': {
        opacity: 0.6,
        fontStyle: 'italic'
      }
    }
  }
}

export default Course